import { useMemo, useRef } from 'react';
import {
  components,
  type ControlProps,
  type GroupBase,
  type Props as ReactSelectProps,
  type SelectComponentsConfig,
  type SelectInstance,
  type SingleValueProps,
  type StylesConfig,
} from 'react-select';

import { StyledSelect, Label } from './index.styled';

// react-select stashes unrecognized props on selectProps; this augmentation (the
// library's documented mechanism) types the ones our custom components read back.
// The empty import pulls react-select/base into the program so the augmentation
// has a module to merge into.
import type {} from 'react-select/base';

declare module 'react-select/base' {
  // oxlint-disable-next-line typescript/consistent-type-definitions -- augmentation requires interface merging
  export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    floatingLabel?: string;
    autoComplete?: string;
  }
}

export const FloatingLabelControl = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: ControlProps<Option, IsMulti, Group>,
) => {
  return (
    <>
      <Label $isFloating={props.hasValue || props.isFocused}>{props.selectProps.floatingLabel}</Label>
      <components.Control {...props} />
    </>
  );
};

const BoldValue = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: SingleValueProps<Option, IsMulti, Group>,
) => {
  return <components.SingleValue className="font-medium" {...props} />;
};

/** Options may carry an isFixed flag to render as a non-removable multi value. */
const isFixedOption = (data: unknown): boolean => !!(data as { isFixed?: boolean } | null)?.isFixed;

const styles: StylesConfig<unknown, boolean, GroupBase<unknown>> = {
  multiValueLabel: (base, state) => {
    return isFixedOption(state.data) ? { ...base, paddingRight: 6 } : base;
  },
  multiValueRemove: (base, state) => {
    if (isFixedOption(state.data)) {
      return {
        ...base,
        display: 'none',
      };
    }
    return base;
  },
};

export type SelectProps<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectProps<Option, IsMulti, Group> & {
  minWidth?: string;
  borderRadius?: number | string;
  borderless?: boolean;
  transparent?: boolean;
  /** Alias for react-select's isDisabled. */
  disabled?: boolean;
  /** Fixed height of the value container. */
  height?: string;
  /** Minimum height of the value container. */
  minHeight?: string;
};

const Select = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  minWidth,
  borderRadius,
  borderless,
  transparent,
  disabled,
  ...props
}: SelectProps<Option, IsMulti, Group>) => {
  const ref = useRef<SelectInstance<Option, IsMulti, Group>>(null);
  const comps = useMemo(() => {
    const tmp: SelectComponentsConfig<Option, IsMulti, Group> = props.floatingLabel
      ? { Control: FloatingLabelControl, SingleValue: BoldValue }
      : { Control: components.Control };

    if (props.autoComplete) {
      tmp.Input = (inputProps) => <components.Input {...inputProps} autoComplete={props.autoComplete} />;
    }

    return tmp;
  }, [props.autoComplete, props.floatingLabel]);

  const handleChange: ReactSelectProps<Option, IsMulti, Group>['onChange'] = (newValue, actionMeta) => {
    props.onChange?.(newValue, actionMeta);
    if (props.floatingLabel && !props.isMulti) {
      ref.current?.blur();
    }
  };

  return (
    <StyledSelect<Option, IsMulti, Group>
      {...props}
      ref={ref}
      isDisabled={disabled}
      classNamePrefix={props.classNamePrefix || 'rsmm'}
      components={comps}
      $minWidth={minWidth}
      $borderRadius={borderRadius}
      $borderless={borderless}
      $transparent={transparent}
      onChange={handleChange}
      placeholder={props.floatingLabel ? '' : props.placeholder}
      styles={{ ...(styles as unknown as StylesConfig<Option, IsMulti, Group>), ...props.styles }}
      maxMenuHeight={240}
    />
  );
};

export default Select;
